// frontend/src/components/dialogs/TripWaypointReorderModal.jsx
// Modal for managing trip waypoint order (Solution C)
// Allows admins to view, reorder, and apply waypoint order changes

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
    X, GripVertical, MapPin, Users, AlertTriangle, Check, Clock, Lock, Unlock,
    RotateCcw, Info, LockIcon, Briefcase, Lightbulb
} from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { ThemedButton } from '../themed';
import ConfirmDialogShell from './ConfirmDialogShell';
import waypointOrderService from '../../services/waypointOrderService';
import translationService from '../../services/translationService';
import { toast } from 'react-toastify';
import { useDateTimePreferences } from '../../hooks/useDateTimePreferences';
import { formatTimeString } from '../../utils/dateTimeFormatter';
import { usePreferences } from '../../contexts/PreferenceContext';

const TripWaypointReorderModal = ({
    isOpen,
    onClose,
    entityId,
    tripId,
    tripInfo = {},
    onOrderChanged
}) => {
    const { t, i18n } = useTranslation();
    const { background, text, border, cn } = useTheme();
    const { formatDate } = useDateTimePreferences();
    const { entitySettings } = usePreferences();

    // State
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState(null);

    // Order data
    const [currentOrder, setCurrentOrder] = useState(null);
    const [editedOrder, setEditedOrder] = useState([]);
    const [hasChanges, setHasChanges] = useState(false);
    const [previewResult, setPreviewResult] = useState(null);

    // Dialogs
    const [showConfirmApply, setShowConfirmApply] = useState(false);
    const [showConfirmReset, setShowConfirmReset] = useState(false);
    const [blockingError, setBlockingError] = useState(null);
    const [changeReason, setChangeReason] = useState('');

    // Drag state
    const [draggedIndex, setDraggedIndex] = useState(null);
    const [dragOverIndex, setDragOverIndex] = useState(null);

    // Vehicle selection
    const [vehicles, setVehicles] = useState([]);
    const [selectedVehicleId, setSelectedVehicleId] = useState(null);
    const [isChangingVehicle, setIsChangingVehicle] = useState(false);

    // Capacity flow (loaded with order, not just from preview)
    const [capacityFlow, setCapacityFlow] = useState(null);

    // Optimization
    const [isOptimizing, setIsOptimizing] = useState(false);

    // Track position changes for visual feedback
    const [positionChanges, setPositionChanges] = useState({});
    const ownerRefreshRef = useRef(onOrderChanged);
    const loadGenerationRef = useRef(0);

    useEffect(() => {
        if (isOpen) ownerRefreshRef.current = onOrderChanged;
    }, [isOpen, onOrderChanged]);

    useEffect(() => {
        if (!isOpen) loadGenerationRef.current += 1;
    }, [isOpen]);

    const defaultLanguage = entitySettings?.entity_default_language || 'en';
    const shouldTranslateContent = entitySettings?.multi_language_enabled === true && i18n.language !== defaultLanguage && entityId;

    const translateWaypointRows = useCallback(async (waypoints = []) => {
        if (!shouldTranslateContent || waypoints.length === 0) {
            return waypoints;
        }

        const waypointItems = waypoints
            .map((waypoint) => ({
                id: waypoint.waypointId || waypoint.id,
                fields: {
                    name: waypoint.name || '',
                    address: waypoint.address || ''
                }
            }))
            .filter((item) => item.id);

        if (waypointItems.length === 0) {
            return waypoints;
        }

        try {
            const translations = await translationService.batchGetTranslations(
                entityId,
                'waypoint',
                waypointItems,
                i18n.language
            );

            return waypoints.map((waypoint) => {
                const translation = translations.get(waypoint.waypointId || waypoint.id);
                return {
                    ...waypoint,
                    displayName: translation?.name?.content || waypoint.displayName || waypoint.name,
                    displayAddress: translation?.address?.content || waypoint.displayAddress || waypoint.address
                };
            });
        } catch (translationError) {
            console.warn('Failed to translate waypoint order rows:', translationError);
            return waypoints;
        }
    }, [entityId, i18n.language, shouldTranslateContent]);

    const translateRouteName = useCallback(async () => {
        if (!shouldTranslateContent || !tripInfo?.routeId) {
            return tripInfo?.routeName || '';
        }

        try {
            const translations = await translationService.batchGetTranslations(
                entityId,
                'route',
                [{
                    id: tripInfo.routeId,
                    fields: {
                        name: tripInfo.routeName || ''
                    }
                }],
                i18n.language
            );

            return translations.get(tripInfo.routeId)?.name?.content || tripInfo.routeName || '';
        } catch (translationError) {
            console.warn('Failed to translate waypoint order route name:', translationError);
            return tripInfo?.routeName || '';
        }
    }, [entityId, i18n.language, shouldTranslateContent, tripInfo?.routeId, tripInfo?.routeName]);

    const translateOrderData = useCallback(async (orderData) => {
        if (!orderData) {
            return orderData;
        }

        const [translatedOrder, routeDisplayName] = await Promise.all([
            translateWaypointRows(orderData.order || []),
            translateRouteName()
        ]);

        return {
            ...orderData,
            routeDisplayName,
            order: translatedOrder
        };
    }, [translateRouteName, translateWaypointRows]);

    // Load the authoritative order first. Vehicle administration is optional
    // and must not make the assigned-driver editor fail with a 403.
    const loadData = useCallback(async () => {
        if (!isOpen || !entityId || !tripId) return;

        const generation = ++loadGenerationRef.current;
        setIsLoading(true);
        setError(null);

        try {
            const orderResult = await waypointOrderService.getCurrentOrder(entityId, tripId);
            const translatedOrderData = await translateOrderData(orderResult.data);
            if (generation !== loadGenerationRef.current) return;

            setCurrentOrder(translatedOrderData);
            setEditedOrder(translatedOrderData?.order || []);
            setSelectedVehicleId(translatedOrderData?.vehicleId || null);
            if (translatedOrderData?.canManageVehicle === true) {
                const vehiclesResult = await waypointOrderService.getAvailableVehicles(entityId, tripId);
                if (generation !== loadGenerationRef.current) return;
                setVehicles(vehiclesResult.data?.vehicles || []);
            } else {
                setVehicles([]);
            }
            // Set initial capacity flow from getCurrentOrder response
            setCapacityFlow(translatedOrderData?.capacityFlow || null);
            setHasChanges(false);
            setPreviewResult(null);
            setPositionChanges({}); // Clear position changes on reload
        } catch (err) {
            if (generation !== loadGenerationRef.current) return;
            console.error('Error loading waypoint order data:', err);
            setError(err.response?.data?.error || t('errorLoadingWaypointOrder'));
        } finally {
            if (generation === loadGenerationRef.current) setIsLoading(false);
        }
    }, [isOpen, entityId, tripId, t, translateOrderData]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const showOrderMutationToast = (result, successKey) => {
        if (result?.sync?.status === 'incomplete') {
            toast.warning(result.sync.message || t('waypointOrderSavedSyncIncomplete'));
            return;
        }

        toast.success(t(successKey));
    };

    const notifyParentOrderChanged = async (payload) => {
        try {
            await ownerRefreshRef.current?.(payload);
        } catch (refreshError) {
            console.error('Parent refresh after waypoint order change failed:', refreshError);
        }
    };

    // Handle drag start
    const handleDragStart = (index) => {
        // Non-reorderable waypoints cannot be moved
        if (!editedOrder[index]?.canReorder) return;
        setDraggedIndex(index);
    };

    // Handle drag over
    const handleDragOver = (e, index) => {
        e.preventDefault();
        // Cannot drop on non-reorderable waypoint positions
        if (!editedOrder[index]?.canReorder) return;
        if (draggedIndex !== null && index !== draggedIndex) {
            setDragOverIndex(index);
        }
    };

    // Handle drop
    const handleDrop = (index) => {
        if (draggedIndex === null || !editedOrder[index]?.canReorder || index === draggedIndex) {
            setDraggedIndex(null);
            setDragOverIndex(null);
            return;
        }

        // Both positions are movable slots. Swap their values so no fixed row
        // can be displaced by splice/insertion across the interval.
        const newOrder = [...editedOrder];
        [newOrder[draggedIndex], newOrder[index]] = [newOrder[index], newOrder[draggedIndex]];

        setEditedOrder(newOrder);
        setHasChanges(true);
        setPreviewResult(null);
        setPositionChanges({}); // Clear optimization indicators on manual drag
        setDraggedIndex(null);
        setDragOverIndex(null);
    };

    const handleMutationError = useCallback(async (err) => {
        const errorData = err.response?.data || {};
        const code = errorData.code || errorData.error;
        const message = errorData.message || t('errorApplyingReorder');
        const unavailableCodes = new Set([
            'WAYPOINT_REORDER_NOT_AUTHORIZED',
            'WAYPOINT_REORDER_NOT_ACTIONABLE',
            'WAYPOINT_REORDER_STATUS_INELIGIBLE',
            'WAYPOINT_REORDER_POSITION_FIXED',
            'WAYPOINT_REORDER_VEHICLE_INVALID'
        ]);

        if (code === 'WAYPOINT_REORDER_VERSION_CONFLICT'
            || code === 'WAYPOINT_REORDER_STATE_CHANGED') {
            setError(message);
            await loadData();
            return;
        }

        if (code === 'WAYPOINT_REORDER_LOCKED') {
            if (currentOrder?.canManageLock === true) {
                setError(message);
                await loadData();
            } else {
                setBlockingError({ message, refreshOwner: true, close: true });
            }
            return;
        }

        if (unavailableCodes.has(code)) {
            setBlockingError({ message, refreshOwner: true, close: true });
            return;
        }

        setError(message);
    }, [currentOrder?.canManageLock, loadData, t]);

    const acknowledgeBlockingError = async () => {
        const behavior = blockingError;
        setBlockingError(null);
        if (behavior?.refreshOwner) {
            await notifyParentOrderChanged({ changeType: 'reorder_availability_changed' });
        }
        if (behavior?.close) onClose();
    };

    // Handle drag end
    const handleDragEnd = () => {
        setDraggedIndex(null);
        setDragOverIndex(null);
    };

    // Preview changes
    const handlePreview = async () => {
        if (!hasChanges) return;

        setIsLoading(true);
        setError(null);

        try {
            const waypointOrder = editedOrder.map(wp => wp.waypointId);
            const result = await waypointOrderService.previewReorder(entityId, tripId, waypointOrder);
            setPreviewResult(result.data);
        } catch (err) {
            console.error('Error previewing reorder:', err);
            const errorData = err.response?.data;
            const code = errorData?.code || errorData?.error;
            if (code === 'WAYPOINT_REORDER_ORDER_INVALID'
                || code === 'WAYPOINT_REORDER_CAPACITY_EXCEEDED') {
                setError(errorData?.message || t('errorPreviewingReorder'));
            } else if (errorData?.errors && errorData.errors.length > 0) {
                setError(errorData.errors.join(', '));
            } else {
                await handleMutationError(err);
            }
            // Still set preview result if data is available (shows capacity flow even on error)
            if (errorData?.data) {
                setPreviewResult(errorData.data);
            }
        } finally {
            setIsLoading(false);
        }
    };

    // Apply changes
    const handleApply = async () => {
        setShowConfirmApply(false);
        setIsSaving(true);
        setError(null);

        try {
            const waypointOrder = editedOrder.map(wp => wp.waypointId);
            const result = await waypointOrderService.applyCustomOrder(
                entityId,
                tripId,
                waypointOrder,
                changeReason || t('manualReorderByAdmin'),
                currentOrder?.version || 0
            );

            showOrderMutationToast(result, 'waypointOrderUpdated');
            setChangeReason('');
            await notifyParentOrderChanged({
                newVersion: result?.newVersion,
                changeType: 'custom_order_applied',
                sync: result?.sync
            });
            await loadData();
        } catch (err) {
            console.error('Error applying reorder:', err);
            await handleMutationError(err);
        } finally {
            setIsSaving(false);
        }
    };

    // Reset to default
    const handleReset = async () => {
        setShowConfirmReset(false);
        setIsSaving(true);
        setError(null);

        try {
            const result = await waypointOrderService.resetToDefault(
                entityId,
                tripId,
                currentOrder?.version || 0,
                t('resetToDefaultByAdmin')
            );

            showOrderMutationToast(result, 'waypointOrderReset');
            await notifyParentOrderChanged({
                newVersion: result?.newVersion,
                changeType: 'reset_to_default',
                sync: result?.sync
            });
            await loadData();
        } catch (err) {
            console.error('Error resetting order:', err);
            await handleMutationError(err);
        } finally {
            setIsSaving(false);
        }
    };

    // Toggle lock
    const handleToggleLock = async () => {
        setIsSaving(true);
        setError(null);

        try {
            const newLocked = !currentOrder?.locked;
            await waypointOrderService.setOrderLock(
                entityId,
                tripId,
                newLocked,
                newLocked ? t('lockedByAdmin') : t('unlockedByAdmin')
            );

            toast.success(newLocked ? t('waypointOrderLocked') : t('waypointOrderUnlocked'));
            await loadData();
        } catch (err) {
            console.error('Error toggling lock:', err);
            setError(err.response?.data?.error || t('errorTogglingLock'));
        } finally {
            setIsSaving(false);
        }
    };

    // Undo changes
    const handleUndo = () => {
        setEditedOrder(currentOrder?.order || []);
        setHasChanges(false);
        setPreviewResult(null);
        setPositionChanges({}); // Clear optimization indicators on undo
    };

    // Handle vehicle change
    const handleVehicleChange = async (newVehicleId) => {
        if (newVehicleId === selectedVehicleId) return;

        setIsChangingVehicle(true);
        setError(null);

        try {
            const result = await waypointOrderService.changeVehicle(
                entityId,
                tripId,
                newVehicleId === 'none' ? null : parseInt(newVehicleId)
            );

            const translatedOrderData = await translateOrderData(result.data);
            setSelectedVehicleId(translatedOrderData?.vehicleId || null);
            setCurrentOrder(translatedOrderData);
            setEditedOrder(translatedOrderData?.order || []);
            setCapacityFlow(translatedOrderData?.capacityFlow || null);

            // Show warning if over-capacity
            const hasOverCapacity = result.data?.capacityFlow && Object.values(result.data.capacityFlow).some(
                wp => wp.onBoardAfterStop > (result.data?.vehicleCapacity || 0) ||
                    (result.data?.vehicleLuggageCapacity > 0 && wp.luggageOnBoardAfterStop > result.data?.vehicleLuggageCapacity)
            );
            if (hasOverCapacity) {
                toast.warning(t('vehicleChangedOverCapacity'));
            } else {
                toast.success(t('vehicleChanged'));
            }

            await notifyParentOrderChanged({ changeType: 'vehicle_changed' });
        } catch (err) {
            console.error('Error changing vehicle:', err);
            setError(err.response?.data?.error || t('errorChangingVehicle'));
        } finally {
            setIsChangingVehicle(false);
        }
    };

    // Handle suggest optimal order
    const handleSuggestOptimal = async () => {
        setIsOptimizing(true);
        setError(null);

        try {
            const result = await waypointOrderService.suggestOptimalOrder(entityId, tripId);

            if (result.data?.alreadyOptimal) {
                toast.info(t('orderAlreadyOptimal'));
                return;
            }

            const suggestedIds = result.data?.suggestedOrder?.map(Number);
            const baselineIds = editedOrder.map((waypoint) => Number(waypoint.waypointId));
            const exactSet = Array.isArray(suggestedIds)
                && suggestedIds.length === baselineIds.length
                && suggestedIds.every(Number.isInteger)
                && new Set(suggestedIds).size === suggestedIds.length
                && suggestedIds.every((id) => baselineIds.includes(id));
            if (!exactSet) {
                setError(t('errorSuggestingOptimalOrder'));
                return;
            }

            // Retain server-computed fixed/movement metadata from the current
            // baseline while applying only a proven complete permutation.
            const currentById = new Map(
                editedOrder.map((waypoint) => [Number(waypoint.waypointId), waypoint])
            );
            const translatedNewOrder = await translateWaypointRows(
                suggestedIds.map((id) => currentById.get(id))
            );

            const changes = {};
            const oldPositions = {};
            editedOrder.forEach((wp, idx) => {
                oldPositions[wp.waypointId] = idx;
            });
            translatedNewOrder.forEach((wp, newIdx) => {
                const oldIdx = oldPositions[wp.waypointId];
                if (oldIdx !== undefined && oldIdx !== newIdx) {
                    changes[wp.waypointId] = {
                        from: oldIdx + 1,
                        to: newIdx + 1,
                        delta: oldIdx - newIdx
                    };
                }
            });
            setPositionChanges(changes);

            setEditedOrder(translatedNewOrder);
            setHasChanges(true);
            setPreviewResult(null);
            if (result.data?.capacityFlow) {
                setCapacityFlow(result.data.capacityFlow);
            }

            toast.success(t('optimalOrderSuggested'));
        } catch (err) {
            console.error('Error suggesting optimal order:', err);
            const errorData = err.response?.data;

            if (errorData?.locked) {
                setError(t('routeIsLockedCannotOptimize'));
            } else if (errorData?.suggestManual) {
                setError(errorData?.reason || t('tooManyWaypointsForOptimization'));
            } else if (errorData?.explanation) {
                // Show detailed explanation for constraint failures
                setError(`${errorData.reason}\n\n${errorData.explanation}`);
            } else if (errorData?.reason) {
                setError(errorData.reason);
            } else {
                setError(errorData?.error || t('errorSuggestingOptimalOrder'));
            }
        } finally {
            setIsOptimizing(false);
        }
    };

    // Format time delta
    const formatTimeDelta = (minutes) => {
        if (!minutes) return '-';
        const sign = minutes > 0 ? '+' : '';
        if (Math.abs(minutes) < 60) {
            return `${sign}${minutes} ${t('min')}`;
        }
        const hrs = Math.floor(Math.abs(minutes) / 60);
        const mins = Math.round(Math.abs(minutes) % 60);
        return `${sign}${hrs}h ${mins}m`;
    };

    // Check if the order is customized (different from default)
    const isCustomOrder = useMemo(() => {
        return currentOrder?.isCustom === true;
    }, [currentOrder]);
    const assignedVehicleInvalid = currentOrder?.assignedVehicleState === 'invalid';

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto">
            {/* Backdrop */}
            <div
                className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
                onClick={onClose}
            />

            {/* Modal */}
            <div className="flex min-h-full items-center justify-center p-4">
                <div className={cn(
                    "relative w-full max-w-3xl rounded-lg shadow-xl",
                    background.primary,
                    "transform transition-all"
                )}>
                    {/* Header */}
                    <div className={cn(
                        "flex items-center justify-between p-4 border-b",
                        border.primary
                    )}>
                        <div className="flex items-center gap-2">
                            <MapPin className={cn("w-5 h-5", text.primary)} />
                            <h2 className={cn("text-lg font-semibold", text.primary)}>
                                {t('waypointOrder')}
                            </h2>
                            {currentOrder?.locked && (
                                <span className="flex items-center gap-1 px-2 py-0.5 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 rounded-full text-xs">
                                    <Lock className="w-3 h-3" />
                                    {t('locked')}
                                </span>
                            )}
                            {isCustomOrder && !currentOrder?.locked && (
                                <span className="flex items-center gap-1 px-2 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 rounded-full text-xs">
                                    {t('customized')}
                                </span>
                            )}
                        </div>
                        <button
                            onClick={onClose}
                            className={cn(
                                "p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700",
                                text.secondary
                            )}
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>

                    {/* Content */}
                    <div className="p-4 max-h-[60vh] overflow-y-auto">
                        {/* Trip info */}
                        <div className={cn("mb-4 p-3 rounded-lg", background.secondary)}>
                            <p className={cn("font-medium", text.primary)}>
                                {currentOrder?.routeDisplayName || tripInfo.routeName || t('trip')} #{tripId}
                            </p>
                            <p className={cn("text-sm", text.muted)}>
                                {tripInfo.tripDate && formatDate(tripInfo.tripDate)} • {tripInfo.departureTime && formatTimeString(tripInfo.departureTime)}
                            </p>
                            {currentOrder?.version !== undefined && (
                                <p className={cn("text-xs mt-1", text.muted)}>
                                    {t('version')}: {currentOrder.version}
                                </p>
                            )}
                        </div>

                        {/* Vehicle selection */}
                        {currentOrder?.canManageVehicle === true && (
                        <div className={cn("mb-4 p-3 rounded-lg flex items-center justify-between flex-wrap gap-3", background.secondary)}>
                            <div className="flex items-center gap-3">
                                <label className={cn("text-sm font-medium", text.primary)}>
                                    {t('vehicle')}:
                                </label>
                                <select
                                    value={selectedVehicleId || 'none'}
                                    onChange={(e) => handleVehicleChange(e.target.value)}
                                    disabled={isChangingVehicle}
                                    className={cn(
                                        "px-3 py-1.5 rounded-lg border text-sm",
                                        background.primary,
                                        border.primary,
                                        text.primary
                                    )}
                                >
                                    <option value="none">{t('noVehicle')}</option>
                                    {vehicles.map(v => (
                                        <option key={v.id} value={v.id}>
                                            {v.vehicleNumber} ({v.capacity} {t('seats')}, {v.luggageCapacity} {t('luggage')})
                                        </option>
                                    ))}
                                </select>
                                {isChangingVehicle && (
                                    <div className="animate-spin h-4 w-4 border-2 border-blue-500 rounded-full border-t-transparent" />
                                )}
                            </div>

                            {/* Current capacity display */}
                            {selectedVehicleId && (
                                <div className={cn("text-sm", text.muted)}>
                                    {t('capacity')}: {currentOrder?.vehicleCapacity || 0} {t('seats')}, {currentOrder?.vehicleLuggageCapacity || 0} {t('luggage')}
                                </div>
                            )}
                        </div>
                        )}

                        {/* Error message */}
                        {assignedVehicleInvalid && (
                            <div className="mb-4 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                                <div className="text-amber-700 dark:text-amber-300 text-sm">
                                    {t('waypointAssignedVehicleUnavailable')}
                                </div>
                            </div>
                        )}
                        {error && (
                            <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                                <div className="text-red-600 dark:text-red-400 text-sm whitespace-pre-line">
                                    {error}
                                </div>
                            </div>
                        )}

                        {/* Waypoint order - Drag and drop */}
                        {isLoading && !currentOrder ? (
                            <div className="flex items-center justify-center py-8">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                            </div>
                        ) : (
                            <div className="space-y-2">
                                <div className="flex items-center justify-between mb-2">
                                    <h3 className={cn("text-sm font-medium", text.primary)}>
                                        {t('currentOrder')}
                                    </h3>
                                    <div className="flex items-center gap-2">
                                        {hasChanges && (
                                            <button
                                                onClick={handleUndo}
                                                className={cn("text-sm flex items-center gap-1", text.muted, "hover:text-blue-600")}
                                            >
                                                <RotateCcw className="w-3 h-3" />
                                                {t('undoChanges')}
                                            </button>
                                        )}
                                        {/* Suggest Optimal Order button */}
                                        {currentOrder?.canSuggest === true && (
                                        <ThemedButton
                                            variant="secondary"
                                            onClick={handleSuggestOptimal}
                                            disabled={isOptimizing || currentOrder?.locked}
                                            icon={Lightbulb}
                                            size="sm"
                                            title={currentOrder?.locked ? t('routeIsLocked') : ''}
                                        >
                                            {isOptimizing ? t('optimizing') : t('suggestOptimalOrder')}
                                        </ThemedButton>
                                        )}
                                    </div>
                                </div>

                                <p className={cn("text-xs mb-3", text.muted)}>
                                    <Info className="w-3 h-3 inline mr-1" />
                                    {t('dragToReorderHelp')}
                                </p>

                                {editedOrder.map((waypoint, index) => (
                                    <div
                                        key={waypoint.waypointId}
                                        draggable={waypoint.canReorder && !currentOrder?.locked}
                                        onDragStart={() => handleDragStart(index)}
                                        onDragOver={(e) => handleDragOver(e, index)}
                                        onDrop={() => handleDrop(index)}
                                        onDragEnd={handleDragEnd}
                                        className={cn(
                                            "flex items-center gap-3 p-3 rounded-lg transition-colors",
                                            background.secondary,
                                            dragOverIndex === index && "border-2 border-blue-500 border-dashed",
                                            draggedIndex === index && "opacity-50",
                                            !waypoint.canReorder && "opacity-75",
                                            // Highlight changed waypoints
                                            positionChanges[waypoint.waypointId] && (
                                                positionChanges[waypoint.waypointId].delta > 0
                                                    ? "ring-2 ring-green-400 dark:ring-green-600 ring-offset-1"
                                                    : "ring-2 ring-amber-400 dark:ring-amber-600 ring-offset-1"
                                            )
                                        )}
                                    >
                                        {/* Drag handle or lock icon */}
                                        <div className={cn(
                                            "flex-shrink-0",
                                            !waypoint.canReorder ? "" : "cursor-grab active:cursor-grabbing",
                                            currentOrder?.locked && "invisible"
                                        )}>
                                            {!waypoint.canReorder ? (
                                                <LockIcon className={cn("w-4 h-4", text.muted)} />
                                            ) : (
                                                <GripVertical className={cn("w-4 h-4", text.muted)} />
                                            )}
                                        </div>

                                        {/* Sequence number */}
                                        <span className={cn(
                                            "w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium",
                                            index === 0
                                                ? "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400"
                                                : "bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400"
                                        )}>
                                            {index + 1}
                                        </span>

                                        {/* Waypoint info */}
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2">
                                                <p className={cn("font-medium truncate", text.primary)}>
                                                    {waypoint.displayName || waypoint.name}
                                                </p>
                                                {/* Position change indicator */}
                                                {positionChanges[waypoint.waypointId] && (
                                                    <span className={cn(
                                                        "px-1.5 py-0.5 rounded text-xs font-medium flex items-center gap-0.5",
                                                        positionChanges[waypoint.waypointId].delta > 0
                                                            ? "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400"
                                                            : "bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400"
                                                    )}>
                                                        {positionChanges[waypoint.waypointId].delta > 0 ? '↑' : '↓'}
                                                        {Math.abs(positionChanges[waypoint.waypointId].delta)}
                                                        <span className="text-[10px] opacity-75 ml-0.5">
                                                            ({t('from')} #{positionChanges[waypoint.waypointId].from})
                                                        </span>
                                                    </span>
                                                )}
                                            </div>
                                            {(waypoint.displayAddress || waypoint.address) && (
                                                <p className={cn("text-xs truncate", text.muted)}>
                                                    {waypoint.displayAddress || waypoint.address}
                                                </p>
                                            )}
                                        </div>

                                        {/* Capacity flow - always visible from capacityFlow state or previewResult */}
                                        {(capacityFlow?.[waypoint.waypointId] || previewResult?.capacityFlow?.[waypoint.waypointId]) && (
                                            <div className="flex items-center gap-2 text-xs">
                                                {/* Rider capacity */}
                                                <span className={cn(
                                                    "px-2 py-0.5 rounded flex items-center gap-1",
                                                    (capacityFlow?.[waypoint.waypointId]?.onBoardAfterStop || previewResult?.capacityFlow?.[waypoint.waypointId]?.onBoardAfterStop) >
                                                        (currentOrder?.vehicleCapacity || 0)
                                                        ? "bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400"
                                                        : "bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400"
                                                )}>
                                                    <Users className="w-3 h-3" />
                                                    {capacityFlow?.[waypoint.waypointId]?.onBoardAfterStop ?? previewResult?.capacityFlow?.[waypoint.waypointId]?.onBoardAfterStop}/{currentOrder?.vehicleCapacity || '?'}
                                                </span>

                                                {/* Luggage capacity - only show if vehicle has luggage capacity */}
                                                {(currentOrder?.vehicleLuggageCapacity || 0) > 0 && (
                                                    <span className={cn(
                                                        "px-2 py-0.5 rounded flex items-center gap-1",
                                                        (capacityFlow?.[waypoint.waypointId]?.luggageOnBoardAfterStop || previewResult?.capacityFlow?.[waypoint.waypointId]?.luggageOnBoardAfterStop || 0) >
                                                            (currentOrder?.vehicleLuggageCapacity || 0)
                                                            ? "bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400"
                                                            : "bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400"
                                                    )}>
                                                        <Briefcase className="w-3 h-3" />
                                                        {capacityFlow?.[waypoint.waypointId]?.luggageOnBoardAfterStop ?? previewResult?.capacityFlow?.[waypoint.waypointId]?.luggageOnBoardAfterStop ?? 0}/{currentOrder?.vehicleLuggageCapacity}
                                                    </span>
                                                )}
                                            </div>
                                        )}

                                        {/* Fixed position badge */}
                                        {!waypoint.canReorder && (
                                            <span className="px-2 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded-full text-xs">
                                                {t('fixed')}
                                            </span>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Preview result */}
                        {previewResult && (
                            <div className={cn(
                                "mt-4 p-3 rounded-lg border",
                                previewResult.valid
                                    ? "border-green-300 dark:border-green-700 bg-green-50 dark:bg-green-900/20"
                                    : "border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/20"
                            )}>
                                <div className="flex items-center gap-2 mb-2">
                                    {previewResult.valid ? (
                                        <>
                                            <Check className="w-4 h-4 text-green-600 dark:text-green-400" />
                                            <span className="font-medium text-green-700 dark:text-green-400">
                                                {t('validReorder')}
                                            </span>
                                        </>
                                    ) : (
                                        <>
                                            <AlertTriangle className="w-4 h-4 text-red-600 dark:text-red-400" />
                                            <span className="font-medium text-red-700 dark:text-red-400">
                                                {t('invalidReorder')}
                                            </span>
                                        </>
                                    )}
                                </div>

                                {previewResult.valid && previewResult.timeImpact !== undefined && (
                                    <p className={cn("text-sm", text.secondary)}>
                                        <Clock className="w-3 h-3 inline mr-1" />
                                        {t('timeImpact')}: {formatTimeDelta(previewResult.timeImpact)}
                                    </p>
                                )}

                                {previewResult.errors?.length > 0 && (
                                    <ul className="mt-2 text-sm text-red-600 dark:text-red-400 list-disc list-inside">
                                        {previewResult.errors.map((err, idx) => (
                                            <li key={idx}>{err}</li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Footer */}
                    <div className={cn(
                        "flex items-center justify-between p-4 border-t",
                        border.primary
                    )}>
                        <div className="flex items-center gap-2">
                            {/* Lock/Unlock button */}
                            {currentOrder?.canManageLock === true && (
                            <ThemedButton
                                variant="ghost"
                                onClick={handleToggleLock}
                                disabled={isSaving}
                                icon={currentOrder?.locked ? Unlock : Lock}
                                size="sm"
                            >
                                {currentOrder?.locked ? t('unlock') : t('lock')}
                            </ThemedButton>
                            )}

                            {/* Reset button */}
                            {isCustomOrder && (
                                <ThemedButton
                                    variant="ghost"
                                    onClick={() => setShowConfirmReset(true)}
                                    disabled={isSaving || currentOrder?.locked || assignedVehicleInvalid}
                                    icon={RotateCcw}
                                    size="sm"
                                >
                                    {t('resetToDefault')}
                                </ThemedButton>
                            )}
                        </div>

                        <div className="flex items-center gap-2">
                            <ThemedButton variant="secondary" onClick={onClose}>
                                {t('close')}
                            </ThemedButton>

                            {hasChanges && (
                                <>
                                    <ThemedButton
                                        variant="secondary"
                                        onClick={handlePreview}
                                        disabled={isLoading || isSaving || assignedVehicleInvalid}
                                    >
                                        {t('preview')}
                                    </ThemedButton>

                                    <ThemedButton
                                        onClick={() => setShowConfirmApply(true)}
                                        disabled={isLoading || isSaving || currentOrder?.locked || assignedVehicleInvalid || !previewResult?.valid}
                                        icon={Check}
                                        title={
                                            currentOrder?.locked
                                                ? t('routeIsLocked')
                                                : !previewResult?.valid
                                                ? t('clickPreviewFirst')
                                                : ''
                                        }
                                    >
                                        {isSaving ? t('saving') : t('applyChanges')}
                                    </ThemedButton>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Confirm Apply Dialog */}
            <ConfirmDialogShell
                isOpen={showConfirmApply}
                onClose={() => setShowConfirmApply(false)}
                onConfirm={handleApply}
                title={t('confirmApplyReorder')}
                confirmLabel={t('apply')}
                confirmIcon={Check}
                confirmVariant="primary"
            >
                <div className={cn("space-y-3", text.secondary)}>
                    <p>{t('confirmApplyReorderMessage')}</p>
                    <div>
                        <label className={cn("block text-sm font-medium mb-1", text.primary)}>
                            {t('changeReason')} ({t('optional')})
                        </label>
                        <input
                            type="text"
                            value={changeReason}
                            onChange={(e) => setChangeReason(e.target.value)}
                            placeholder={t('changeReasonPlaceholder')}
                            className={cn(
                                "w-full px-3 py-2 rounded-lg border text-sm",
                                background.primary,
                                border.primary,
                                text.primary
                            )}
                        />
                    </div>
                </div>
            </ConfirmDialogShell>

            {/* Confirm Reset Dialog */}
            <ConfirmDialogShell
                isOpen={showConfirmReset}
                onClose={() => setShowConfirmReset(false)}
                onConfirm={handleReset}
                title={t('confirmResetOrder')}
                confirmLabel={t('reset')}
                confirmIcon={RotateCcw}
                confirmVariant="warning"
            >
                <p className={text.secondary}>{t('confirmResetOrderMessage')}</p>
            </ConfirmDialogShell>

            <ConfirmDialogShell
                isOpen={Boolean(blockingError)}
                onClose={acknowledgeBlockingError}
                onConfirm={acknowledgeBlockingError}
                title={t('waypointOrder')}
                confirmLabel={t('close')}
                confirmVariant="primary"
            >
                <p className={text.secondary}>{blockingError?.message}</p>
            </ConfirmDialogShell>
        </div>
    );
};

export default TripWaypointReorderModal;
